import React from 'react';
import {
  makeStyles,
  Typography,
  Box,
  Hidden,
  Button,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import clsx from 'clsx';
import { withTranslation } from 'config/i18n';
import Link from 'components/shared/Link';
import Pagination from '@material-ui/lab/Pagination';
import PaginationItem from '@material-ui/lab/PaginationItem';

const useStyles = makeStyles((theme) => ({
  ul: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexGrow: '1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationUlOverride: {
    justifyContent: 'center',
  },
  itemLi: {},
  numberButtonOverride: {
    color: theme.palette.secondary.dark,
    fontSize: theme.typography.pxToRem(16),
    borderRadius: '0',
    padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(8)}`,
    margin: `0 ${theme.typography.pxToRem(8)}`,
    minWidth: 20,
    '&:hover': {
      background: 'transparent',
    },
  },
  selectedButton: {
    color: theme.palette.primary.main,
    background: 'transparent !important',
    '&:hover': {
      background: 'transparent !important',
    },
    [theme.breakpoints.up('md')]: {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
  },
  arrowBtn: {
    margin: `0 ${theme.typography.pxToRem(16)}`,
    color: theme.palette.secondary.dark,
    borderRadius: '0',
    '&:hover': {
      background: 'transparent',
    },
  },
  arrowBox: {},
  prevBox: {},
  nextBox: {},
  arrowIcon: {
    color: theme.palette.grey[700],
    width: theme.typography.pxToRem(26),
    height: theme.typography.pxToRem(26),
  },
  prevIcon: {
    marginRight: theme.typography.pxToRem(6),
    transform: 'rotate(180deg)',
  },
  nextIcon: {
    marginLeft: theme.typography.pxToRem(6),
  },
  arrowText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  arrowLink: {
    color: 'inherit',
  },
  disabledArrowBtn: {
    '& svg': {
      color: alpha(theme.palette.common.black, 0.26),
    },
  },
  disabledArrowText: {
    color: alpha(theme.palette.common.black, 0.26),
  },
  disabledLink: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  hideForSmall: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  hideForLarge: {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));
const ERPagination = ({
  t, data, url, link, handleChange,
}) => {
  const classes = useStyles();

  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Pagination
        classes={{
          ul: classes.paginationUlOverride,
        }}
        page={data.active_page}
        count={data.pages_count}
        boundaryCount={1}
        renderItem={(item) => (
          <>
            {item.type == 'page' && (
              <>
                {isSmallDown ? (
                  <>
                    {item.selected ? (
                      <PaginationItem
                        {...item}
                        className={`${
                          item.selected ? classes.selectedButton : ''
                        } ${classes.numberButtonOverride}`}
                      >
                        {item.page}
                      </PaginationItem>
                    ) : (
                      ''
                    )}
                    {item.page == data.pages_count && (
                      <>
                        /
                        <PaginationItem
                          {...item}
                          classes={{ root: classes.numberButtonOverride }}
                        >
                          {data.pages_count}
                        </PaginationItem>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <PaginationItem
                      type="page"
                      className={`${classes.numberButtonOverride}`}
                      classes={{
                        selected: classes.selectedButton,
                      }}
                      component={link ? Link : Button}
                      to={link && `${url}?page=${item.page}`}
                      href={link && `${url}?page=${item.page}`}
                      {...item}
                      onClick={() => handleChange(item.page)}
                    />
                  </>
                )}
              </>
            )}
            {item.type == 'previous' && !item.disabled && (
              <>
                {link ? (
                  <Link
                    href={{ pathname: url, query: { page: item.page } }}
                    className={`${classes.arrowLink} ${
                      item.disabled ? classes.disabledLink : ''
                    }`}
                    onClick={() => handleChange(item.page)}
                  >
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.prevBox)}
                    >
                      <ArrowRightAltIcon
                        className={`${classes.arrowIcon} ${classes.prevIcon}
                          ${item.disabled ? classes.disabledArrowText : ''}`}
                      />
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}
                      >
                        {t('pagination:prev')}
                      </Typography>
                    </Box>
                  </Link>
                ) : (
                  <Button
                    className={classes.arrowLink}
                    onClick={() => {
                      handleChange(item.page);
                    }}
                  >
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.prevBox)}
                    >
                      <ArrowRightAltIcon
                        className={`${clsx(
                          classes.arrowIcon,
                          classes.prevIcon,
                        )}`}
                      />
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}
                      >
                        {t('pagination:prev')}
                      </Typography>
                    </Box>
                  </Button>
                )}
              </>
            )}
            {item.type == 'next' && !item.disabled && (
              <>
                {link ? (
                  <Link
                    href={{ pathname: url, query: { page: item.page } }}
                    className={`${classes.arrowLink} ${
                      item.disabled ? classes.disabledLink : ''
                    }`}
                    onClick={() => handleChange(item.page)}
                  >
                    <Box
                      display="flex"
                      className={`${clsx(classes.arrowBox, classes.nextBox)} ${
                        item.disabled ? classes.disabledArrowText : ''
                      }`}
                    >
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}
                      >
                        {t('pagination:next')}
                      </Typography>
                      <ArrowRightAltIcon
                        className={`${classes.arrowIcon} ${classes.nextIcon}
                        ${item.disabled ? classes.disabledArrowText : ''}`}
                      />
                    </Box>
                  </Link>
                ) : (
                  <Button
                    className={classes.arrowLink}
                    onClick={() => {
                      handleChange(item.page);
                    }}
                  >
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.nextBox)}
                    >
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}
                      >
                        {t('pagination:next')}
                      </Typography>
                      <ArrowRightAltIcon
                        className={clsx(classes.arrowIcon, classes.nextIcon)}
                      />
                    </Box>
                  </Button>
                )}
              </>
            )}
            {(item.type == 'start-ellipsis'
              || item.type === 'end-ellipsis') && <Hidden smDown>…</Hidden>}
          </>
        )}
      />
    </>
  );
};

export default withTranslation('pagination')(ERPagination);
